import React, { useState, useEffect } from 'react';
import { DollarSign, Clock, MapPin, ChevronRight, Loader2, TrendingUp } from 'lucide-react';
import { customersApi } from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/helpers';

/**
 * CustomerPricingHistory - Shows last price charged per service type
 * Helps answer "What did I charge last time?"
 * 
 * Props:
 * - customerId: Customer ID to fetch pricing history for
 * - onViewJob: Optional callback when user clicks to view a job
 * - onUsePrevious: Optional callback when user wants to use previous pricing
 *                  Called with (serviceTypeId, { price, measurement, unit })
 */
const CustomerPricingHistory = ({ customerId, onViewJob, onUsePrevious }) => {
  const [pricingHistory, setPricingHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (customerId) {
      loadPricingHistory();
    }
  }, [customerId]);

  const loadPricingHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await customersApi.getPricingHistory(customerId);
      setPricingHistory(response.data || []);
    } catch (err) {
      console.error('Error loading pricing history:', err);
      // Don't show error for 404 (no history yet)
      if (err.response?.status !== 404) {
        setError('Failed to load pricing history');
      }
      setPricingHistory([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="animate-spin text-gray-400" size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 text-sm">{error}</p>
        <button 
          onClick={loadPricingHistory}
          className="mt-2 text-sm text-primary-600 hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (pricingHistory.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg">
        <DollarSign size={32} className="mx-auto text-gray-300 mb-2" />
        <p className="text-gray-500 text-sm">No pricing history yet</p>
        <p className="text-gray-400 text-xs mt-1">
          Completed jobs will appear here with their pricing
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp size={18} className="text-green-600" />
        <h3 className="font-medium text-gray-900">Pricing History</h3>
        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
          {pricingHistory.length} service{pricingHistory.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Pricing List */}
      <div className="space-y-2">
        {pricingHistory.map((item) => (
          <div 
            key={item.serviceTypeId}
            className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-start justify-between gap-3">
              {/* Service Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-medium text-gray-900">
                    {item.serviceTypeName}
                  </h4>
                  {item.pricingTemplate && (
                    <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">
                      {item.pricingTemplate.replace('per-', '').replace('sqft', 'sq ft').replace('linear-ft', 'linear ft')}
                    </span>
                  )}
                </div>

                {/* Measurement & Date */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                  {item.measurementValue && item.measurementUnit && (
                    <span className="flex items-center gap-1">
                      <MapPin size={12} />
                      {item.measurementValue.toLocaleString()} {item.measurementUnit}
                    </span>
                  )}
                  {item.lastJobDate && (
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {formatDate(item.lastJobDate)}
                    </span>
                  )}
                </div>

                {/* Job Address */}
                {item.jobAddress && (
                  <p className="text-xs text-gray-400 mt-1 truncate">
                    {item.jobAddress}
                  </p>
                )}
              </div>

              {/* Price */}
              <div className="text-right flex-shrink-0">
                <p className="text-lg font-semibold text-green-600">
                  {formatCurrency(item.lastPrice)}
                </p>
                {item.lastUnitPrice > 0 && item.measurementUnit && (
                  <p className="text-xs text-gray-500">
                    ${item.lastUnitPrice.toFixed(4)}/{item.measurementUnit === 'sq ft' ? 'sq ft' : 'ln ft'}
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100">
              {onUsePrevious && (
                <button
                  onClick={() => onUsePrevious(item.serviceTypeId, {
                    price: item.lastPrice,
                    unitPrice: item.lastUnitPrice,
                    measurement: item.measurementValue,
                    unit: item.measurementUnit,
                  })}
                  className="flex-1 text-xs text-primary-600 hover:text-primary-700 font-medium py-1.5 px-2 rounded hover:bg-primary-50 transition-colors"
                >
                  Use This Pricing
                </button>
              )}
              {onViewJob && item.lastJobId && (
                <button
                  onClick={() => onViewJob(item.lastJobId)}
                  className="text-xs text-gray-500 hover:text-gray-700 py-1.5 px-2 rounded hover:bg-gray-100 transition-colors flex items-center gap-1"
                >
                  View Job
                  <ChevronRight size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomerPricingHistory;